import { useEffect, useState } from "react";
import type { LiveQuestion } from "../../../shared/types";
import { Button } from "../../components/ui/Button";
import { Face } from "../../components/ui/Face";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { firstOf } from "./roomWords";

/** Replies to the questions a live left: each drafted to edit, then sent into the asker's own thread. */
export function RepliesSheet({
  webinarId,
  questions,
  onClose,
  onSent,
}: {
  webinarId: string;
  questions: LiveQuestion[];
  onClose: () => void;
  onSent: (ids: string[]) => void;
}) {
  const toast = useToast();
  // Why kept: the questions are the ones waiting when the sheet opened; each is drafted once.
  const [list] = useState(questions);
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    for (const q of list) {
      api.webinars
        .draftAnswer(webinarId, { registrant_id: q.registrantId, question: q.body })
        .then((r) => alive && setText((was) => (was[q.id] ? was : { ...was, [q.id]: r.draft })))
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [webinarId, list]);

  const ready = list.filter((q) => (text[q.id] ?? "").trim());
  const send = async () => {
    if (busy) return;
    setBusy(true);
    const done: string[] = [];
    for (const q of ready) {
      try {
        const { contactId } = await api.talk.directFor(q.registrantId);
        await api.talk.sendDirect(contactId, { channel: "chat", body: (text[q.id] ?? "").trim(), clientId: crypto.randomUUID() });
        done.push(q.id);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : `We couldn't send that to ${firstOf(q.name)}`);
      }
    }
    setBusy(false);
    if (done.length) {
      onSent(done);
      const names = ready.filter((q) => done.includes(q.id)).map((q) => firstOf(q.name));
      toast({ text: `Replies sent to ${names.length > 2 ? `${names.length} people` : names.join(" and ")}`, icon: "send" });
      onClose();
    }
  };

  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title="Send replies"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" disabled={busy || ready.length === 0} onClick={() => void send()}>
            {busy ? "Sending…" : `Send ${ready.length} ${ready.length === 1 ? "reply" : "replies"}`}
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 16 }}>
        {list.map((q) => (
          <div key={q.id} className="field">
            <div className="row g8">
              <Face name={q.name} size={28} />
              <b><bdi>{q.name}</bdi></b>
            </div>
            <p className="small muted">“{q.body}”</p>
            <textarea dir="auto"
              className="inp"
              rows={3}
              aria-label={`Your reply to ${firstOf(q.name)}`}
              placeholder={text[q.id] === undefined ? "Writing a reply…" : `Your reply to ${firstOf(q.name)}`}
              value={text[q.id] ?? ""}
              onChange={(e) => setText((was) => ({ ...was, [q.id]: e.target.value }))}
            />
          </div>
        ))}
      </div>
    </Sheet>
  );
}
